import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Moon, Sun, Rocket, Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import DeployArea from './DeployArea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeployAuth = ({ onBack }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, loginForm);
      const userData = response.data.user;
      
      // Check if user has deploy access
      if (!userData.has_deploy_access && userData.role !== 'supreme_admin') {
        toast.error('Nu ai acces la zona Deploy/Updates. Contactează administratorul.');
        setLoading(false);
        return;
      }

      // Store token
      localStorage.setItem('deploy_token', response.data.access_token);
      setUser(userData);
      setIsAuthenticated(true);
      toast.success(`Bine ai venit, ${userData.full_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Autentificare eșuată');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('deploy_token');
    setUser(null);
    setIsAuthenticated(false);
    toast.info('Te-ai deconectat');
  };

  if (isAuthenticated && user) {
    return (
      <DeployArea
        user={user}
        onBack={onBack}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={onBack}
              className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-elegant-md">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">Deploy/Updates</h1>
              <p className="text-xs text-muted-foreground">Autentificare necesară</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleDarkMode}
            className="rounded-xl border-border hover:bg-accent-light/50 hover:border-accent transition-smooth"
          >
            {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content - Login Form */}
      <main className="relative z-10 px-6 pb-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-card border-border rounded-2xl p-8 shadow-elegant-lg">
            <div className="mb-6 p-4 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-xl">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Acces Restricționat</h3>
                  <p className="text-sm text-muted-foreground">
                    Doar utilizatorii autorizați de Admin Suprem pot accesa zona Deploy/Updates.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <Lock className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Autentificare Deploy
              </h2>
              <p className="text-sm text-muted-foreground">
                Introdu credențialele pentru a accesa zona de deploy
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nume utilizator</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Introdu numele de utilizator"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                  className="rounded-xl"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Parolă</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                disabled={loading}
              >
                {loading ? 'Se autentifică...' : 'Autentificare'}
              </Button>
            </form>

            <p className="mt-4 text-xs text-center text-muted-foreground">
              Contactează administratorul pentru acces la această zonă
            </p>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DeployAuth;
